import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Si2tLogo from "./Si2tLogo";
import "./SiteHeader.css";

const LINKS = [
  { label: "Accueil", to: "/", id: "top" },
  { label: "Expertises", to: "/#expertises", id: "expertises" },
  { label: "Projets", to: "/#etudes", id: "etudes" },
  { label: "Méthode", to: "/#methode", id: "methode" },
  { label: "Contact", to: "/#contact", id: "contact" },
];

const SECTION_IDS = ["expertises", "etudes", "methode", "renfort", "contact"];

const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("top");
  const { pathname, hash } = useLocation();
  const firstLinkRef = useRef(null);
  const headerRef = useRef(null);
  const burgerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 920px)");
    const onBreakpointChange = () => {
      if (!mobile.matches) setOpen(false);
    };
    onBreakpointChange();
    mobile.addEventListener("change", onBreakpointChange);
    return () => mobile.removeEventListener("change", onBreakpointChange);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const hadMenuOpenClass = document.body.classList.contains("menu-open");
    document.body.style.overflow = "hidden";
    document.body.classList.add("menu-open");
    firstLinkRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        burgerRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const controls = Array.from(headerRef.current?.querySelectorAll("a[href], button:not([disabled])") || [])
        .filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      const outsideMenu = !controls.includes(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || outsideMenu)) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && (document.activeElement === last || outsideMenu)) {
        e.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.toggle("menu-open", hadMenuOpenClass);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (pathname !== "/" || !("IntersectionObserver" in window)) return undefined;
    const visible = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visible.set(e.target.id, e.isIntersecting));
        const current = SECTION_IDS.find((id) => visible.get(id));
        setActiveId(current || "top");
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    const timer = setTimeout(() => {
      SECTION_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) io.observe(el);
      });
    }, 200);
    return () => {
      clearTimeout(timer);
      io.disconnect();
    };
  }, [pathname]);

  const isActive = (link) => {
    if (pathname !== "/") return false;
    const id = link.id === "renfort" ? "methode" : link.id;
    return (activeId === "renfort" ? "methode" : activeId) === id;
  };

  const closeMenu = () => setOpen(false);

  return (
    <header ref={headerRef} className={`site-header ${scrolled ? "is-scrolled" : ""} ${open ? "is-open" : ""}`} data-testid="site-header">
      <div className="site-header-inner">
        <Link to="/" onClick={closeMenu} className="header-logo" data-testid="header-logo" aria-label="SI²T — retour à l'accueil">
          <Si2tLogo />
        </Link>

        <nav className="header-nav" aria-label="Navigation principale">
          {LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={closeMenu}
              className={`header-link ${isActive(link) ? "is-active" : ""}`}
              aria-current={isActive(link) ? "page" : undefined}
              data-testid={`nav-link-${link.id}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="header-actions">
          <Link to="/#contact" onClick={closeMenu} className="btn btn-primary header-cta" data-testid="header-cta">
            Discuter de votre projet
            <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
          <button
            ref={burgerRef}
            type="button"
            className="header-burger"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="header-mobile-panel"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="header-burger"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <div
        id="header-mobile-panel"
        className={`header-mobile-panel ${open ? "is-open" : ""}`}
        data-testid="header-mobile-panel"
        aria-hidden={!open}
      >
        <nav className="header-mobile-nav" aria-label="Navigation mobile">
          {LINKS.map((link, i) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={closeMenu}
              ref={i === 0 ? firstLinkRef : undefined}
              className="header-mobile-link"
              tabIndex={open ? 0 : -1}
              data-testid={`mobile-nav-link-${link.id}`}
            >
              <span className="header-mobile-index">0{i + 1}</span>
              {link.label}
            </Link>
          ))}
        </nav>
        <Link to="/#contact" onClick={closeMenu} className="btn btn-primary header-mobile-cta" tabIndex={open ? 0 : -1} data-testid="mobile-header-cta">
          Discuter de votre projet
          <ArrowRight size={16} strokeWidth={2.2} />
        </Link>
        <div className="header-mobile-foot">
          <Link to="/galerie" onClick={closeMenu} className="header-mobile-sub" tabIndex={open ? 0 : -1}>Galerie de projets</Link>
          <Link to="/documents" onClick={closeMenu} className="header-mobile-sub" tabIndex={open ? 0 : -1}>Documents</Link>
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
